import React, { useRef, useEffect, useMemo } from 'react';
import {
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import type { SortingStrategy } from '@dnd-kit/sortable';
import { clsx } from 'clsx';
import { useCrossDnd } from '../CrossDndContext';
import { SLBPosition } from '@/types';
import { Spinner } from '../helpers/Spinner';
import { ColumnHeader } from '../helpers/ColumnHeader';
import { HEADER_ROW_HEIGHT_PX } from './TableSkeleton';
import { VirtualizedGrid } from '@/components/shared/VirtualizedGrid';
import { highlightText, formatCellValue } from '@/utils/inventory';
import { borderShadow, SELECTION_FILL, ROW_HIGHLIGHT_SHADOW, HEADER_COL_SELECTED_SHADOW } from '@/utils/cell-selection-styles';

import type { ColumnDef } from '@tanstack/react-table';
import { getColumnId } from '@/types/table.types';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
} from '@heroicons/react/24/outline';

// No-op strategy: items do not displace during drag. Visual feedback is
// provided by the DragOverlay ghost + the blue drop indicator.
const noDisplacementStrategy: SortingStrategy = () => null;

const GripDotsIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 16 16" fill="currentColor" className={className}>
    <circle cx="5.5" cy="3" r="1.2" />
    <circle cx="10.5" cy="3" r="1.2" />
    <circle cx="5.5" cy="8" r="1.2" />
    <circle cx="10.5" cy="8" r="1.2" />
    <circle cx="5.5" cy="13" r="1.2" />
    <circle cx="10.5" cy="13" r="1.2" />
  </svg>
);

const L2CP_GROUP_ID = 'lentToCounterparties';
const L2CP_INNER_IDS = ['l2cpQuantity', 'l2cpAmountEur', 'l2cpRate', 'l2cpGrossRate', 'l2cpSnpDelta'];
const L2CP_INNER_SET = new Set(L2CP_INNER_IDS);
const L2CP_ALL_SET = new Set([L2CP_GROUP_ID, ...L2CP_INNER_IDS]);

const NUMERIC_COLUMNS = new Set([
  'price', 'quantity', 'settledQuantity', 'quantityT1', 'quantityT2', 'quantityTx',
  'lentToCounterparties', 'l2cpQuantity', 'l2cpAmountEur', 'l2cpRate', 'l2cpGrossRate', 'l2cpSnpDelta',
  'pendingTransfers', 'pendingSettlementQuantity', 'cimbEq2Quantity', 'cimbSfQuantity', 'cimbSf3Quantity', 'velocityLQuantity', 'velocityMQuantity',
  'leverageRate', 'valueEUR', 'benchmarkFee', 'quantityOnLoan', 'activeUtilisation',
  'currencyBenchmark', 'rebateRate', 'dailyPotentialEUR', 'adtv30D', 'adtvMultiple', 'adtvAdjustedPotentialEUR',
]);
const L2CP_DND_PREFIX = 'l2cp:';
const L2CP_COLLAPSE_COL_WIDTH = 32;
// eslint-disable-next-line react-refresh/only-export-components -- prefix helper paired with table component; splitting needs ~4 import-site updates
export const stripL2cpPrefix = (id: string): string =>
  id.startsWith(L2CP_DND_PREFIX) ? id.slice(L2CP_DND_PREFIX.length) : id;
// eslint-disable-next-line react-refresh/only-export-components -- prefix helper paired with table component; splitting needs ~4 import-site updates
export const isL2cpDndId = (id: string): boolean => id.startsWith(L2CP_DND_PREFIX);

const FILTERABLE_COLUMN_IDS = new Set([
  'accountId',
  'counterparty',
  'currency',
  'exchange',
  'caStatus',
  'caType',
  'brokerAccountName',
  'brokerSettlementCounterparty',
  'quantity',
  'settledQuantity',
  'quantityT1',
  'quantityT2',
  'quantityTx',
  'leverageRate',
  'valueEUR',
  'benchmarkFee',
  'activeUtilisation',
  'quantityOnLoan',
  'rebateRate',
  'currencyBenchmark',
  'dailyPotentialEUR',
  'adtv30D',
  'adtvMultiple',
  'adtvAdjustedPotentialEUR',
  'lentToCounterparties',
  'l2cpQuantity',
  'l2cpAmountEur',
  'l2cpRate',
  'l2cpGrossRate',
  'l2cpSnpDelta',
  'pendingTransfers',
  'cimbEq2Quantity',
  'cimbSfQuantity',
  'cimbSf3Quantity',
  'velocityLQuantity',
  'velocityMQuantity',
  'exDate',
]);

const FILTER_ICON_EXTRA_PX = 24;
const PIN_ICON_EXTRA_PX = 22;

type InventoryColumnDef = ColumnDef<SLBPosition, unknown>;

const getColId = (col: InventoryColumnDef): string => getColumnId(col);

const getColWidth = (col: InventoryColumnDef, columnWidths: Record<string, number>, pinnedSet?: Set<string>): number => {
  const cid = getColId(col);
  const base = columnWidths[cid] || 100;
  return base
    + (FILTERABLE_COLUMN_IDS.has(cid) ? FILTER_ICON_EXTRA_PX : 0)
    + (pinnedSet && pinnedSet.has(cid) && cid !== 'rowNumber' ? PIN_ICON_EXTRA_PX : 0);
};

type SortableHeaderWrapperProps = {
  id: string;
  children: (dragHandle: React.ReactNode) => React.ReactNode;
  enabled: boolean;
  showHandle?: boolean;
  className?: string;
  style?: React.CSSProperties;
  onContextMenu?: (e: React.MouseEvent) => void;
};

const SortableHeaderWrapper: React.FC<SortableHeaderWrapperProps> = ({
  id,
  children,
  enabled,
  showHandle = true,
  className,
  style,
  onContextMenu,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging,
  } = useSortable({ id, disabled: !enabled });
  const { activeDragSource, tableOverColumnId } = useCrossDnd();
  // Show blue indicator when this column is the hover target during any drag
  const showDropIndicator = tableOverColumnId === id && activeDragSource != null;
  const dragStyle: React.CSSProperties = {
    ...style,
    // No transform — noDisplacementStrategy means columns don't shift during drag
    visibility: (isDragging && activeDragSource === 'table') ? 'hidden' as const : undefined,
  };
  const handle = enabled && showHandle ? (
    <span
      className="flex flex-shrink-0 items-center justify-center cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 self-center"
      {...attributes}
      {...listeners}
      tabIndex={0}
      role="button"
      aria-label="Drag to reorder column"
    >
      <GripDotsIcon className="h-4 w-4" />
    </span>
  ) : null;
  return (
    <div
      ref={setNodeRef}
      className={className}
      style={dragStyle}
      onContextMenu={onContextMenu}
      data-column-id={id}
    >
      {showDropIndicator && (
        <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-blue-500 rounded-full z-10 pointer-events-none" />
      )}
      {children(handle)}
    </div>
  );
};

type InventoryTableProps = {
  data: SLBPosition[];
  columns: InventoryColumnDef[];
  sorting: { id: string; desc: boolean }[];
  onSortChange: (sorting: { id: string; desc: boolean }[]) => void;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  columnWidths: Record<string, number>;
  isLoading: boolean;
  error: Error | null;
  hoveredColumn: string | null;
  onHoveredColumnChange: (column: string | null) => void;
  onShowScrollTop: (show: boolean) => void;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  l2cpGroupExpanded?: boolean;
  onToggleL2cpGroup?: () => void;
  allL2cpNestedChecked?: boolean;
  activeFilterColumnIds?: string[];
  onFilterColumnClick?: (columnId: string) => void;
  highlightedColumnIds?: string[];
  pinnedLeft?: string[];
  pinnedRight?: string[];
  onContextMenuColumn?: (e: React.MouseEvent, columnId: string) => void;
  onContextMenuCell?: (e: React.MouseEvent, columnId: string, item: SLBPosition) => void;
  onPinColumn?: (columnId: string, side: 'left' | 'right' | null) => void;
  isCellSelected?: (row: number, col: number) => boolean;
  isCellActive?: (row: number, col: number) => boolean;
  isRowSelected?: (row: number) => boolean;
  isColSelected?: (col: number) => boolean;
  getCellBorders?: (row: number, col: number) => import('@/hooks/useCellSelection').CellBorders | null;
  colIdToIndex?: Map<string, number>;
  /** DO NOT REMOVE — intentionally unused inside component body.
   *  Changing this value forces React.memo to re-render so that
   *  stable selection query callbacks (isCellSelected, etc.) pick up fresh ref data. */
  selectionTrigger?: unknown;
  ensureCellSelectedForContextMenu?: (columnId: string, rowIndex: number) => void;
  onCellMouseDown?: (e: React.MouseEvent, columnId: string, rowIndex: number) => void;
  onCellMouseEnter?: (e: React.MouseEvent, columnId: string, rowIndex: number) => void;
  onCellMouseUp?: () => void;
  isColumnsTabActive?: boolean;
};

export const InventoryTable: React.FC<InventoryTableProps> = React.memo(({
  data,
  columns,
  sorting,
  onSortChange,
  searchQuery,
  onSearchQueryChange,
  columnWidths,
  isLoading,
  error,
  hoveredColumn,
  onHoveredColumnChange,
  onShowScrollTop,
  scrollContainerRef,
  l2cpGroupExpanded = false,
  onToggleL2cpGroup,
  allL2cpNestedChecked = false,
  activeFilterColumnIds,
  onFilterColumnClick,
  highlightedColumnIds,
  pinnedLeft = [],
  pinnedRight = [],
  onContextMenuColumn,
  onContextMenuCell,
  onPinColumn,
  isCellSelected,
  isCellActive,
  isRowSelected,
  isColSelected,
  getCellBorders,
  colIdToIndex,
  ensureCellSelectedForContextMenu,
  onCellMouseDown,
  onCellMouseEnter,
  onCellMouseUp,
  isColumnsTabActive = false,
}) => {
  const internalParentRef = useRef<HTMLDivElement>(null);
  const parentRef = scrollContainerRef || internalParentRef;

  const pinnedLeftSet = useMemo(() => new Set(pinnedLeft), [pinnedLeft]);
  const pinnedRightSet = useMemo(() => new Set(pinnedRight), [pinnedRight]);
  const allPinnedSet = useMemo(() => new Set([...pinnedLeft, ...pinnedRight]), [pinnedLeft, pinnedRight]);

  const { leftCols, centerCols, rightCols, leftWidth, rightWidth } = useMemo(() => {
    const left: InventoryColumnDef[] = [];
    const center: InventoryColumnDef[] = [];
    const right: InventoryColumnDef[] = [];
    for (const col of columns) {
      const cid = getColId(col);
      if (pinnedLeftSet.has(cid)) left.push(col);
      else if (pinnedRightSet.has(cid)) right.push(col);
      else center.push(col);
    }
    let lw = 0;
    for (const col of left) lw += getColWidth(col, columnWidths, allPinnedSet) + 12;
    let rw = 0;
    for (const col of right) rw += getColWidth(col, columnWidths, allPinnedSet) + 12;
    return { leftCols: left, centerCols: center, rightCols: right, leftWidth: lw + (left.length > 0 ? 10 : 0), rightWidth: rw + (right.length > 0 ? 16 : 0) };
  }, [columns, columnWidths, pinnedLeftSet, pinnedRightSet, allPinnedSet]);

  const hasPinnedLeft = leftCols.length > 0;
  const hasPinnedRight = rightCols.length > 0;

  const toggleSort = (columnId: string) => {
    if (columnId === 'rowNumber') return;
    const current = sorting.find((s) => s.id === columnId);
    if (!current) {
      onSortChange([{ id: columnId, desc: false }]);
    } else if (!current.desc) {
      onSortChange([{ id: columnId, desc: true }]);
    } else {
      onSortChange([]);
    }
  };

  // LR-600: unlike TradesTable / AvailabilityTable this component never hides the
  // scroll container (the spinner is a bodyOverride below, and the cold-load
  // skeleton lives in InventoryTabPanel, which unmounts the table outright), so the
  // listener always attaches. `isLoading` in the deps + the sync call are here to
  // re-sync the button at load boundaries — the panel outlives the table and would
  // otherwise keep a stale `true` — and to keep the three tables identical.
  useEffect(() => {
    const scrollElement = parentRef.current;
    if (!scrollElement) return;
    const handleScroll = () => {
      onShowScrollTop(scrollElement.scrollTop > 500);
    };
    handleScroll();
    scrollElement.addEventListener('scroll', handleScroll);
    return () => scrollElement.removeEventListener('scroll', handleScroll);
  }, [onShowScrollTop, parentRef, isLoading]);

  // Ctrl+Arrow: scroll to edges (like Google Sheets navigation)
  useEffect(() => {
    const el = parentRef.current;
    if (!el) return;
    let animId = 0;
    const smoothScroll = (targetTop: number, targetLeft: number) => {
      cancelAnimationFrame(animId);
      const startTop = el.scrollTop;
      const startLeft = el.scrollLeft;
      const distTop = targetTop - startTop;
      const distLeft = targetLeft - startLeft;
      const duration = 250; // ms (roughly 2x faster than browser default ~500ms)
      const start = performance.now();
      const step = (now: number) => {
        const t = Math.min((now - start) / duration, 1);
        const ease = t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2; // easeInOutQuad
        el.scrollTop = startTop + distTop * ease;
        el.scrollLeft = startLeft + distLeft * ease;
        if (t < 1) animId = requestAnimationFrame(step);
      };
      animId = requestAnimationFrame(step);
    };
    const handler = (e: KeyboardEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          smoothScroll(0, el.scrollLeft);
          break;
        case 'ArrowDown':
          e.preventDefault();
          smoothScroll(el.scrollHeight, el.scrollLeft);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          smoothScroll(el.scrollTop, 0);
          break;
        case 'ArrowRight':
          e.preventDefault();
          smoothScroll(el.scrollTop, el.scrollWidth);
          break;
      }
    };
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
      cancelAnimationFrame(animId);
    };
  }, [parentRef]);

  // Column index lookup map — avoids O(n) indexOf calls in render loops
  const colIndexMap = useMemo(
    () => new Map(columns.map((c, i) => [c, i])),
    [columns]
  );
  const colIndexOf = (col: InventoryColumnDef): number => colIndexMap.get(col) ?? -1;

  const isL2cpGroupColumn = (columnId: string): boolean => L2CP_ALL_SET.has(columnId);
  const isFirstL2cpCol = (col: InventoryColumnDef): boolean => getColId(col) === L2CP_GROUP_ID;

  // Dynamically detect the last L2CP column based on actual column order (handles reorder)
  const lastL2cpColId = useMemo(() => {
    let lastId = '';
    for (const col of columns) {
      const cid = (col as { accessorKey?: string }).accessorKey || col.id || '';
      if (L2CP_INNER_SET.has(cid)) lastId = cid;
    }
    return lastId;
  }, [columns]);
  const isLastL2cpCol = (col: InventoryColumnDef): boolean => getColId(col) === lastL2cpColId;

  const renderCell = (column: InventoryColumnDef, item: SLBPosition, virtualItemIndex: number) => {
    const accessorKey = (column as { accessorKey?: string }).accessorKey;
    const columnId = accessorKey || column.id || '';
    if (!accessorKey) {
      return column.cell && typeof column.cell === 'function'
        ? (column.cell as (ctx: { row: { original: SLBPosition; index: number } }) => React.ReactNode)(
            { row: { original: item, index: virtualItemIndex } }
          )
        : '';
    }
    const cellValue = item[accessorKey as keyof SLBPosition];
    const cellContent = column.cell && typeof column.cell === 'function'
      ? (column.cell as (ctx: { getValue: () => unknown; row: { original: SLBPosition } }) => React.ReactNode)(
            { getValue: () => cellValue, row: { original: item } }
          )
      : String(cellValue || '');
    const hasSearchIcon = column.meta?.hasSearchIcon;
    const stringValue = String(cellValue || '');
    let finalContent = cellContent;
    if (searchQuery && cellValue !== null && cellValue !== undefined) {
      const query = searchQuery.trim().toLowerCase();
      const queryWithoutCommas = query.replace(/,/g, '');
      const queryHasSymbols = query.includes('$') || query.includes('€') || query.includes('%');
      let isMatch = false;
      if (typeof cellValue === 'number') {
        const formattedValue = formatCellValue(column, cellValue);
        const formattedLower = formattedValue.toLowerCase();
        const formattedWithoutCommas = formattedLower.replace(/,/g, '');
        if (queryHasSymbols) {
          isMatch = formattedLower.includes(query) || formattedWithoutCommas.includes(queryWithoutCommas);
        } else {
          const formattedWithoutSymbols = formattedWithoutCommas.replace(/[$€%]/g, '');
          isMatch = formattedLower.includes(query) || formattedWithoutCommas.includes(queryWithoutCommas) || formattedWithoutSymbols.includes(queryWithoutCommas);
        }
      } else {
        isMatch = stringValue.toLowerCase().includes(query);
      }
      if (isMatch && (typeof cellValue === 'string' || typeof cellValue === 'number')) {
        if (React.isValidElement(cellContent) && (cellContent.type as { name?: string }).name === 'TruncatedCell') {
          const highlighted = highlightText(stringValue, searchQuery);
          finalContent = React.cloneElement(cellContent as React.ReactElement, { content: <span className="text-gray-900">{highlighted}</span> });
        } else if (typeof cellValue === 'number' && React.isValidElement(cellContent)) {
          const formattedText = formatCellValue(column, cellValue);
          finalContent = React.cloneElement(cellContent as React.ReactElement, { children: highlightText(formattedText, searchQuery) });
        } else if (hasSearchIcon && React.isValidElement(cellContent)) {
          finalContent = React.cloneElement(cellContent as React.ReactElement, { children: highlightText(stringValue, searchQuery) });
        } else {
          const highlighted = highlightText(stringValue, searchQuery);
          if (React.isValidElement(cellContent)) {
            finalContent = React.cloneElement(cellContent as React.ReactElement, { children: highlighted });
          } else {
            finalContent = highlighted;
          }
        }
      }
    }
    if (hasSearchIcon) {
      const isFilterIcon = columnId === 'isin' || columnId === 'sedol';
      const IconComponent = isFilterIcon ? FunnelIcon : MagnifyingGlassIcon;
      const iconSize = isFilterIcon ? 'h-2.5 w-2.5' : 'h-3 w-3';
      return (
        <span className="group relative">
          {finalContent}
          <button
            onClick={(e) => { e.stopPropagation(); onSearchQueryChange(stringValue); }}
            className="absolute top-1/2 ml-1 -translate-y-1/2 rounded p-0.5 opacity-0 transition-opacity hover:bg-gray-200 group-hover:opacity-100"
            title={`Search for this ${columnId.toUpperCase()}`}
          >
            <IconComponent className={`${iconSize} text-gray-600`} />
          </button>
        </span>
      );
    }
    return finalContent;
  };

  const headerColumnIds = useMemo(() => {
    return columns
      .map((c: InventoryColumnDef) => getColId(c))
      .filter((id: string) => !(l2cpGroupExpanded && isColumnsTabActive && L2CP_INNER_SET.has(id)));
  }, [columns, l2cpGroupExpanded, isColumnsTabActive]);
  /** Render a single header cell given a column object. Uses full `columns` array for L2CP index checks. */
  const renderHeaderCell = (column: InventoryColumnDef, _colIndex: number): React.ReactNode => {
    const columnId = getColId(column);
    const sortState = sorting.find((s) => s.id === columnId);
    const width = getColWidth(column, columnWidths, allPinnedSet);
    const rawHeader = column.header !== undefined && column.header !== null ? column.header : 'Column';
    const headerContent = typeof rawHeader === 'function' ? (rawHeader as () => React.ReactNode)() : rawHeader;
    const isL2cpExpandable = isL2cpGroupColumn(columnId) && columnId !== L2CP_GROUP_ID;
    const align = columnId === L2CP_GROUP_ID ? 'left' : (NUMERIC_COLUMNS.has(columnId) ? 'right' : 'left');
    const headerTooltip = column.meta?.headerTooltip as string | undefined;
    const isL2cp = isL2cpGroupColumn(columnId);
    const showL2cpBg = isL2cp && l2cpGroupExpanded;
    const showFocusHighlight = highlightedColumnIds?.includes(columnId) ?? false;
    const isFirstL2cp = isFirstL2cpCol(column);
    const isLastL2cp = isLastL2cpCol(column);
    const showExpandRight = isFirstL2cp && onToggleL2cpGroup && !l2cpGroupExpanded && !allL2cpNestedChecked;
    const hasButtonRight = showExpandRight;
    const pinSide = columnId === 'rowNumber' ? null : pinnedLeftSet.has(columnId) ? 'left' : pinnedRightSet.has(columnId) ? 'right' : null;
    const headerWidth = (hasButtonRight ? width - 28 : width) - (pinSide ? PIN_ICON_EXTRA_PX : 0);
    const headerGapClass = columnId === 'rowNumber' ? '' : isL2cp ? (isFirstL2cp ? 'ml-1.5' : isLastL2cp && !l2cpGroupExpanded ? 'mr-1.5' : '') : 'px-1.5 box-content';
    const justifyEnd = showL2cpBg && isL2cpExpandable;
    const isLentToCounterpartiesCol = columnId === L2CP_GROUP_ID;
    const headerPaddingL2cp = isFirstL2cp ? 'pl-2 pr-1.5' : isLastL2cp ? 'pl-1.5 pr-1.5' : 'px-1.5';
    const headerPaddingClass = isL2cp ? headerPaddingL2cp : (!showL2cpBg && isLentToCounterpartiesCol && sortState ? 'pl-2' : '');
    const headerJustifyFocus = isL2cp ? (columnId === L2CP_GROUP_ID ? 'justify-start' : 'justify-end') : (align === 'right' ? 'justify-end' : 'justify-start');
    const headerBgClass = showL2cpBg || showFocusHighlight
      ? `self-stretch flex items-center -my-2 py-2 ${showL2cpBg ? (justifyEnd ? 'justify-end' : 'justify-start') : headerJustifyFocus} ${showFocusHighlight ? 'bg-blue-50 animate-column-highlight-out' : 'bg-blue-50'}`
      : `${isFirstL2cpCol(column) ? 'justify-start' : 'justify-end'}`;
    const isL2cpInner = L2CP_INNER_SET.has(columnId);
    const sortableId = isL2cpInner ? `${L2CP_DND_PREFIX}${columnId}` : columnId;
    const sortableEnabled = isL2cpInner
      ? isColumnsTabActive && l2cpGroupExpanded
      : isColumnsTabActive;
    const headerColIdx = colIdToIndex?.get(columnId);
    const isHeaderColSelected = headerColIdx !== undefined && isColSelected?.(headerColIdx);
    return (
      <SortableHeaderWrapper
        key={columnId}
        id={sortableId}
        enabled={sortableEnabled}
        showHandle={columnId !== 'rowNumber' && (!isL2cpInner || l2cpGroupExpanded)}
        className={clsx('relative flex items-center', headerGapClass, headerPaddingClass, headerBgClass, isHeaderColSelected && 'self-stretch -mb-2 pb-2')}
        style={{
          width: `${width}px`,
          minWidth: `${width}px`,
          ...(isHeaderColSelected ? { boxShadow: HEADER_COL_SELECTED_SHADOW } : undefined),
        }}
        onContextMenu={onContextMenuColumn ? (e) => onContextMenuColumn(e, columnId) : undefined}
      >
        {(dragHandle) => (
          <>
            {(() => {
              const header = (
                <ColumnHeader
                  columnId={columnId}
                  headerText={headerContent}
                  headerTooltip={headerTooltip}
                  width={isColumnsTabActive ? headerWidth - 20 : headerWidth}
                  sortState={sortState}
                  onSort={toggleSort}
                  isLoading={isLoading}
                  hasError={!!error}
                  hoveredColumn={hoveredColumn}
                  onHover={onHoveredColumnChange}
                  align={align}
                  shrinkToContent={isL2cp}
                  sortIconPosition={isLentToCounterpartiesCol ? 'left' : undefined}
                  filterable={FILTERABLE_COLUMN_IDS.has(columnId)}
                  onFilterClick={onFilterColumnClick}
                  hasActiveFilter={activeFilterColumnIds?.includes(columnId)}
                  filterIconAfterText={isLentToCounterpartiesCol}
                  hideControls={isColumnsTabActive}
                  dragHandle={dragHandle}
                  pinSide={pinSide}
                  onUnpin={onPinColumn ? () => onPinColumn(columnId, null) : undefined}
                />
              );
              return hasButtonRight ? <div className="min-w-0">{header}</div> : header;
            })()}
            {showExpandRight && (
              <button type="button" onClick={(e) => { e.stopPropagation(); onToggleL2cpGroup!(); }}
                className="ml-0.5 flex-shrink-0 rounded p-0.5 text-gray-600 hover:bg-gray-200 hover:text-gray-900"
                title="Expand group" aria-label="Expand Lent to Counterparties group">
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            )}
          </>
        )}
      </SortableHeaderWrapper>
    );
  };

  /** Render a single body cell given a column object. */
  const renderBodyCell = (column: InventoryColumnDef, item: SLBPosition, rowIndex: number, _colIndex: number): React.ReactNode => {
    const columnId = getColId(column);
    const width = getColWidth(column, columnWidths, allPinnedSet);
    const shouldTruncate = columnId !== 'accountId' && columnId !== 'shortName' && columnId !== 'rowNumber';
    const isL2cpCell = isL2cpGroupColumn(columnId);
    const showL2cpCellBg = isL2cpCell && l2cpGroupExpanded;
    const showFocusHighlight = highlightedColumnIds?.includes(columnId) ?? false;
    const isFirst = isFirstL2cpCol(column);
    const isLast = isLastL2cpCol(column);
    const cellGapClass = columnId === 'rowNumber' ? '' : isL2cpCell ? (isFirst ? 'ml-1.5 pl-3' : isLast && !l2cpGroupExpanded ? 'mr-1.5' : '') : 'px-1.5 box-content';
    const cellPaddingL2cp = isFirst ? 'pl-2 pr-1.5' : isLast ? 'pl-1.5 pr-1.5' : 'px-1.5';
    const cellPaddingClass = isL2cpCell ? cellPaddingL2cp : (!showL2cpCellBg && columnId === L2CP_GROUP_ID ? 'pl-2' : '');
    const isCellInPinZone = pinnedLeftSet.has(columnId) || pinnedRightSet.has(columnId) || columnId === 'rowNumber';
    const cellJustifyFocus = isL2cpCell ? (columnId !== L2CP_GROUP_ID ? 'justify-end' : 'justify-start') : (NUMERIC_COLUMNS.has(columnId) ? 'justify-end' : 'justify-start');
    const cellBgClass = showL2cpCellBg || showFocusHighlight
      ? `${showL2cpCellBg ? 'bg-blue-50' : ''} ${showFocusHighlight ? 'bg-blue-50 animate-column-highlight-out' : ''}`
      : '';
    const ci = colIdToIndex?.get(columnId);
    const selected = ci !== undefined && isCellSelected?.(rowIndex, ci);
    const active = ci !== undefined && isCellActive?.(rowIndex, ci);
    const showSelectionFill = selected && !active;
    const isRowNumberCol = columnId === 'rowNumber';
    const rowHighlighted = isRowNumberCol && isRowSelected?.(rowIndex);
    const borders = selected && ci !== undefined ? getCellBorders?.(rowIndex, ci) : null;
    const selectionShadow = borders ? borderShadow(borders) : undefined;
    return (
      <div
        key={columnId}
        className={clsx(
          'relative text-sm self-stretch flex items-center min-w-0',
          !isCellInPinZone && '-my-2 py-2',
          cellJustifyFocus,
          shouldTruncate && 'overflow-hidden text-ellipsis whitespace-nowrap',
          cellGapClass, cellPaddingClass, cellBgClass
        )}
        style={{
          width: `${width}px`,
          minWidth: `${width}px`,
          ...(selected && selectionShadow
            ? {
                boxShadow: selectionShadow,
                zIndex: 1,
                ...(showSelectionFill ? { background: SELECTION_FILL } : undefined),
              }
            : undefined),
          ...(rowHighlighted && !selected ? { boxShadow: ROW_HIGHLIGHT_SHADOW } : undefined),
          cursor: isRowNumberCol ? 'pointer' : 'default',
        }}
        onMouseDown={onCellMouseDown ? (e) => onCellMouseDown(e, columnId, rowIndex) : undefined}
        onMouseEnter={onCellMouseEnter ? (e) => onCellMouseEnter(e, columnId, rowIndex) : undefined}
        onMouseUp={onCellMouseUp}
        onContextMenu={onContextMenuCell ? (e) => {
          if (e.ctrlKey || e.metaKey) return;
          e.preventDefault();
          e.stopPropagation();
          ensureCellSelectedForContextMenu?.(columnId, rowIndex);
          onContextMenuCell(e, columnId, item);
        } : undefined}
      >
        {renderCell(column, item, rowIndex)}
      </div>
    );
  };

  /** IDs for the L2CP inner sortable context (visible inner columns only, prefixed) */
  const l2cpInnerSortableIds = useMemo(() => {
    if (!l2cpGroupExpanded) return [];
    return columns
      .filter((c: InventoryColumnDef) => L2CP_INNER_SET.has(getColId(c)))
      .map((c: InventoryColumnDef) => `${L2CP_DND_PREFIX}${getColId(c)}`);
  }, [columns, l2cpGroupExpanded]);

  /** Render a set of header cells for a column group, inserting a ghost placeholder when a sidebar drag hovers over a column. */
  const renderHeaderGroup = (cols: InventoryColumnDef[]): React.ReactNode => {
    const result: React.ReactNode[] = [];
    let l2cpInnerBuf: InventoryColumnDef[] = [];

    const flushL2cpBuf = () => {
      if (l2cpInnerBuf.length === 0) return;
      // Wrap L2CP inner columns in their own SortableContext
      const innerCols = l2cpInnerBuf;
      l2cpInnerBuf = [];
      result.push(
        <SortableContext
          key="__l2cp-inner-sortable__"
          items={l2cpInnerSortableIds}
          strategy={noDisplacementStrategy}
        >
          {innerCols.map((col) => renderHeaderCell(col, colIndexOf(col)))}
        </SortableContext>
      );
      // Collapse column after the L2CP expanded section
      if (l2cpGroupExpanded && onToggleL2cpGroup) {
        result.push(
          <div
            key="__l2cp-collapse__"
            className="relative flex items-center justify-center self-stretch -my-2 py-2 mr-1.5 bg-blue-50"
            style={{ width: `${L2CP_COLLAPSE_COL_WIDTH}px`, minWidth: `${L2CP_COLLAPSE_COL_WIDTH}px` }}
          >
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onToggleL2cpGroup(); }}
              className="flex-shrink-0 rounded p-0.5 text-gray-600 hover:bg-blue-100 hover:text-gray-900"
              title="Collapse group"
              aria-label="Collapse Lent to Counterparties group"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
          </div>
        );
      }
    };

    for (const col of cols) {
      const colId = getColId(col);
      const isL2cpInner = L2CP_INNER_SET.has(colId);

      if (isL2cpInner && l2cpGroupExpanded && isColumnsTabActive) {
        l2cpInnerBuf.push(col);
      } else {
        flushL2cpBuf();
        result.push(renderHeaderCell(col, colIndexOf(col)));
        // Collapse column after the last L2CP inner column (non-sortable path)
        if (isL2cpInner && l2cpGroupExpanded && !isColumnsTabActive && isLastL2cpCol(col) && onToggleL2cpGroup) {
          result.push(
            <div
              key="__l2cp-collapse__"
              className="relative flex items-center justify-center self-stretch -my-2 py-2 mr-1.5 bg-blue-50"
              style={{ width: `${L2CP_COLLAPSE_COL_WIDTH}px`, minWidth: `${L2CP_COLLAPSE_COL_WIDTH}px` }}
            >
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onToggleL2cpGroup(); }}
                className="flex-shrink-0 rounded p-0.5 text-gray-600 hover:bg-blue-100 hover:text-gray-900"
                title="Collapse group"
                aria-label="Collapse Lent to Counterparties group"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </button>
            </div>
          );
        }
      }
    }
    flushL2cpBuf();
    return result;
  };

  /** Render a set of body cells for a column group, with L2CP collapse spacer. */
  const renderBodyGroup = (cols: InventoryColumnDef[], item: SLBPosition, rowIndex: number): React.ReactNode => {
    if (!l2cpGroupExpanded) {
      return cols.map((col) => renderBodyCell(col, item, rowIndex, colIndexOf(col)));
    }
    const result: React.ReactNode[] = [];
    for (const col of cols) {
      result.push(renderBodyCell(col, item, rowIndex, colIndexOf(col)));
      if (isLastL2cpCol(col)) {
        result.push(
          <div
            key="__l2cp-collapse-spacer__"
            className="self-stretch -my-2 py-2 mr-1.5 bg-blue-50"
            style={{ width: `${L2CP_COLLAPSE_COL_WIDTH}px`, minWidth: `${L2CP_COLLAPSE_COL_WIDTH}px` }}
          />
        );
      }
    }
    return result;
  };

  const renderRow = (item: SLBPosition, rowIndex: number, key: React.Key, style?: React.CSSProperties) => (
    <div key={key} style={style}>
      <div className="relative flex items-center border-b border-gray-200 hover:bg-gray-50" style={{ height: '38px' }}>
        {hasPinnedLeft && (
          <div
            className="sticky left-0 z-[2] flex items-center self-stretch bg-white pl-3 pr-3 py-2 shadow-[4px_0_6px_-2px_rgba(0,0,0,0.1)]"
            style={{ minWidth: `${leftWidth}px`, width: `${leftWidth}px` }}
          >
            {renderBodyGroup(leftCols, item, rowIndex)}
          </div>
        )}
        <div className={clsx('flex items-center py-2', hasPinnedLeft ? 'pl-2' : 'pl-4', hasPinnedRight ? 'pr-2' : 'pr-4')}>
          {renderBodyGroup(centerCols, item, rowIndex)}
        </div>
        {hasPinnedRight && (
          <div
            className="sticky right-0 z-[2] flex items-center self-stretch bg-white pl-3 pr-7 py-2 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.1)]"
            style={{ minWidth: `${rightWidth}px`, width: `${rightWidth}px` }}
          >
            {renderBodyGroup(rightCols, item, rowIndex)}
          </div>
        )}
      </div>
    </div>
  );

  const headerNode = (
    <SortableContext items={headerColumnIds} strategy={noDisplacementStrategy}>
      {hasPinnedLeft && (
        <div
          className="sticky left-0 z-[12] flex items-center self-stretch bg-gray-50 pl-3 pr-3 shadow-[4px_0_6px_-2px_rgba(0,0,0,0.1)]"
          style={{ minWidth: `${leftWidth}px`, width: `${leftWidth}px` }}
        >
          {renderHeaderGroup(leftCols)}
        </div>
      )}
      <div className={clsx('flex items-center self-stretch py-2', hasPinnedLeft ? 'pl-2' : 'pl-4', hasPinnedRight ? 'pr-2' : 'pr-4')}>
        {renderHeaderGroup(centerCols)}
      </div>
      {hasPinnedRight && (
        <div
          className="sticky right-0 z-[12] flex items-center self-stretch bg-gray-50 pl-3 pr-7 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.1)]"
          style={{ minWidth: `${rightWidth}px`, width: `${rightWidth}px` }}
        >
          {renderHeaderGroup(rightCols)}
        </div>
      )}
    </SortableContext>
  );

  let bodyOverride: React.ReactNode = null;
  if (isLoading) {
    bodyOverride = <Spinner message="Loading data..." />;
  } else if (error) {
    bodyOverride = (
      <div className="ml-[37vw] flex items-center py-12">
        <div className="text-center">
          <div className="mb-2 text-red-600">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="mb-2 text-lg font-medium text-gray-900">Failed to load data</h3>
          <p className="mb-4 text-sm text-gray-500">
            {error instanceof Error ? error.message : 'An error occurred while loading inventory data'}
          </p>
          <button onClick={() => window.location.reload()}
            className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <VirtualizedGrid<SLBPosition>
      rows={data}
      estimateRowHeight={38}
      overscan={5}
      scrollContainerRef={parentRef}
      scrollContainerProps={{ 'data-table-scroll-container': true } as React.HTMLAttributes<HTMLDivElement>}
      header={headerNode}
      headerClassName="shrink-0"
      headerStyle={{ height: `${HEADER_ROW_HEIGHT_PX}px`, minHeight: `${HEADER_ROW_HEIGHT_PX}px` }}
      bodyOverride={bodyOverride}
      initialPaintFallback={20}
      renderRow={({ row: item, index, virtualRow, style }) =>
        renderRow(item, index, virtualRow?.key ?? index, style)
      }
    />
  );
});
